import React, { useMemo, useState } from 'react';
import Navbar from '../components/Navbar';
import TickerTape from '../components/TickerTape';
import Footer from '../components/Footer';
import CompanyCard from '../components/CompanyCard';
import CompanyTable from '../components/CompanyTable';
import OrderBookModal from '../components/modals/OrderBookModal';
import BuyModal from '../components/modals/BuyModal';
import { CATEGORIES } from '../mock';
import { useApp } from '../context/AppContext';
import { Search, LayoutGrid, Table2, Sparkles, Star, Loader2 } from 'lucide-react';

const SORTS = {
  'Highest Valuation': (a, b) => b.valuation - a.valuation,
  'Share Price': (a, b) => b.lastPrice - a.lastPrice,
  '24h Gainers': (a, b) => b.change - a.change,
  'Trading Volume': (a, b) => b.volume - a.volume,
};

export default function MarketsPage() {
  const { watchlist, companies, loading } = useApp();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('Highest Valuation');
  const [view, setView] = useState('cards');
  const [cat, setCat] = useState('All');
  const [orderBookId, setOrderBookId] = useState(null);
  const [buy, setBuy] = useState(null);
  const orderBookCompany = companies.find((c) => c.id === orderBookId) || null;

  const filtered = useMemo(() => {
    let list = companies.filter((c) => !c.hidden);
    if (cat === 'Watchlist') list = list.filter((c) => watchlist.includes(c.id));
    else if (cat !== 'All') list = list.filter((c) => c.category === cat);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(q) || c.ticker.toLowerCase().includes(q));
    }
    return list.sort(SORTS[sort]);
  }, [query, sort, cat, watchlist, companies]);

  const chips = [...CATEGORIES, 'Watchlist'];

  const stats = [
    ['TOTAL TRACKED UNICORNS', '$842B+', 'USD', 'text-white'],
    ['24H SECONDARY VOLUME', '$37.9M', 'USD', 'text-[var(--green)]'],
    ['FUNDING SETTLEMENT', 'Crypto + Fedwire', '', 'text-[var(--orange)]'],
    ['REGULATORY STATUS', 'SEC Rule 506(c)', '', 'text-[#60a5fa]'],
  ];

  return (
    <div className="min-h-screen">
      <TickerTape />
      <Navbar />

      {/* Hero */}
      <section className="relative border-b border-[var(--border)] overflow-hidden">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="max-w-[1320px] mx-auto px-5 py-16 relative">
          <div className="max-w-2xl animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#3a2a18] bg-[#1c1710] text-[10.5px] font-mono tracking-[0.14em] text-[var(--orange)]">
              <Sparkles size={12} /> INSTITUTIONAL ALTERNATIVE TRADING SYSTEM (ATS)
            </div>
            <h1 className="font-display font-bold text-[52px] leading-[1.02] tracking-tight text-white mt-6 uppercase">
              Unicorn liquidity has arrived.
            </h1>
            <p className="text-[15px] text-[var(--muted)] leading-relaxed mt-5">
              Join the new generation of accredited investors and institutions buying and selling private US company shares. Live secondary market order books for <span className="text-white font-medium">SpaceX, OpenAI, Stripe, ByteDance</span> and more.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-10">
              {stats.map(([k, v, u, c]) => (
                <div key={k}>
                  <div className="text-[9.5px] font-mono tracking-[0.12em] text-[var(--muted-2)] leading-tight">{k}</div>
                  <div className={`font-mono font-semibold text-[20px] mt-2 ${c}`}>{v} {u && <span className="text-[11px] text-[var(--muted-2)]">{u}</span>}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Controls */}
      <section className="max-w-[1320px] mx-auto px-5 pt-8">
        <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="relative w-full md:max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-2)]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search companies (e.g. SpaceX, OpenAI, AI)..."
              className="w-full bg-[var(--panel)] border border-[var(--border)] rounded-lg pl-9 pr-3 py-2.5 text-[13px] text-white placeholder:text-[var(--muted-2)] focus:outline-none focus:border-[var(--orange)] transition-colors"
            />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-mono tracking-widest text-[var(--muted-2)]">SORT:</span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="bg-[var(--panel)] border border-[var(--border)] rounded-lg px-3 py-2 text-[12.5px] text-white focus:outline-none focus:border-[var(--orange)] transition-colors cursor-pointer"
              >
                {Object.keys(SORTS).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex items-center rounded-lg border border-[var(--border)] overflow-hidden">
              <button onClick={() => setView('cards')} className={`flex items-center gap-1.5 px-3 py-2 text-[12px] transition-colors ${view === 'cards' ? 'bg-[var(--panel-2)] text-white' : 'text-[var(--muted)] hover:text-white'}`}>
                <LayoutGrid size={14} /> Cards
              </button>
              <button onClick={() => setView('table')} className={`flex items-center gap-1.5 px-3 py-2 text-[12px] transition-colors ${view === 'table' ? 'bg-[var(--panel-2)] text-white' : 'text-[var(--muted)] hover:text-white'}`}>
                <Table2 size={14} /> Table
              </button>
            </div>
          </div>
        </div>

        {/* Category chips */}
        <div className="flex flex-wrap gap-2 mt-5">
          {chips.map((c) => {
            const active = cat === c;
            const isWatch = c === 'Watchlist';
            return (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11.5px] font-medium border transition-colors ${
                  active
                    ? 'bg-[var(--orange)] border-[var(--orange)] text-white'
                    : 'bg-[var(--panel)] border-[var(--border)] text-[var(--muted)] hover:text-white hover:border-[#3a3a42]'
                } ${isWatch ? 'uppercase tracking-wide' : 'uppercase tracking-wide text-[10.5px]'}`}
              >
                {isWatch && <Star size={12} className={active ? 'fill-white' : ''} />}
                {c}
              </button>
            );
          })}
        </div>
      </section>

      {/* Grid / Table */}
      <section className="max-w-[1320px] mx-auto px-5 pt-6 pb-4">
        {loading ? (
          <div className="text-center py-24 text-[var(--muted)]">
            <Loader2 size={30} className="mx-auto text-[var(--orange)] animate-spin" />
            <p className="mt-4 text-[14px]">Loading markets...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-[var(--muted)]">
            <Star size={32} className="mx-auto text-[var(--muted-2)]" />
            <p className="mt-4 text-[14px]">No companies match your filters.</p>
          </div>
        ) : view === 'cards' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((c) => (
              <CompanyCard key={c.id} company={c} onOrderBook={(x) => setOrderBookId(x.id)} onBuy={setBuy} />
            ))}
          </div>
        ) : (
          <CompanyTable companies={filtered} onOrderBook={(x) => setOrderBookId(x.id)} onBuy={setBuy} />
        )}
      </section>

      <Footer />

      <OrderBookModal company={orderBookCompany} open={!!orderBookId} onClose={() => setOrderBookId(null)} />
      <BuyModal company={buy} open={!!buy} onClose={() => setBuy(null)} />
    </div>
  );
}
